import React from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { int } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable } from '../components/ui';

// Marketing email subscribers: customers who ticked the marketing opt-in at
// checkout. Served live from the ordering platform's Datastore (the nightly
// snapshot doesn't carry this data), so the list is always current. Owners
// see only their own stores' subscribers (scoped server-side).

const dateShort = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

// Local wall-clock "YYYY-MM-DD HH:mm" for the CSV.
const dateCsv = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function SubscribersPage() {
  const { locations } = useFilters();
  const { data, loading, error } = useReport('marketing-subscribers', {});

  const locName = new Map(locations.map(l => [Number(l.ID), l.NAME]));
  const rows = (data?.subscribers || []).map((s, i) => ({
    ...s,
    location: s.locationId != null ? (locName.get(s.locationId) || `#${s.locationId}`) : '',
    __key: i,
  }));

  const last30 = rows.filter(s =>
    s.subscribeDate && Date.now() - new Date(s.subscribeDate).getTime() < 30 * 864e5).length;

  return (
    <div className="grid" style={{ gap: 14 }}>
      {error ? <ErrorBox message={error} /> :
        loading || !data ? <Spinner label="Loading subscriber list…" /> : (
          <>
            <div className="grid kpi-grid">
              <KpiCard label="Email Subscribers" value={int(data.count)} sub="opted into marketing at checkout" />
              <KpiCard label="New in Last 30 Days" value={int(last30)} sub="by subscribe date" />
            </div>
            <Card
              tour="subs-table"
              title="Subscribed Members"
              sub="live from the ordering platform · newest first · CSV exports the current sort"
            >
              {rows.length === 0 ? <Empty>No subscribers yet.</Empty> : (
                <DataTable
                  csvName="email-subscribers.csv"
                  defaultSort={{ key: 'subscribeDate', dir: 'desc' }}
                  maxRows={50}
                  columns={[
                    { key: 'subscribeDate', label: 'Subscribed', render: r => dateShort(r.subscribeDate), csv: r => dateCsv(r.subscribeDate) },
                    { key: 'firstName', label: 'First Name' },
                    { key: 'lastName', label: 'Last Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone', render: r => r.phone || '—' },
                    { key: 'city', label: 'City', render: r => r.city || '—' },
                    { key: 'location', label: 'Location', render: r => r.location || '—' },
                  ]}
                  rows={rows}
                />
              )}
            </Card>
          </>
        )}
    </div>
  );
}
